import { createColumnHelper } from '@tanstack/react-table';
import { isNumber } from 'fp-ts/number';
import { OnboardingAuditField } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Body, Stack, Tag, Typography } from '@optimus-web/core';
import { tagVariantHandler, TaskStatusTag } from './AuditStatusTag';
import { MarkdownNotes } from './MarkdownNotes';
import { RowActionCell } from './overrideOnboardingAudit';
import { AuditNameLink } from './AuditNameLink';
import { AuditName } from './AuditName';

const columnHelper = createColumnHelper<OnboardingAuditField>();

export const getColumns = (inDialog?: boolean) => {
    const columns = [
        columnHelper.accessor('area', {}),
        columnHelper.accessor('fieldIdentifier', {
            header: () => <LocalizedText textKey={TextKey.GoLiveAuditsTableColumnTask} />,
            cell: ({ row: { original }, getValue }) => {
                if (inDialog) {
                    return <AuditName fieldIdentifier={original.fieldIdentifier} />;
                }

                return <AuditNameLink fieldIdentifier={getValue()} />;
            },
            meta: {
                align: 'start',
                width: 350,
            },
        }),
        columnHelper.display({
            header: () => <LocalizedText textKey={TextKey.GoLiveAuditsTableColumnStatus} />,
            id: 'status',
            cell: ({ row: { original } }) => {
                const hasCount = isNumber(original.count);
                const hasTotal = isNumber(original.totalCount);

                if (!hasCount) {
                    return <TaskStatusTag status={original.status} />;
                }

                return (
                    <Stack spacing="50">
                        <Tag variant={tagVariantHandler(original.status)}>
                            {hasTotal
                                ? `${original.count} ${UnicodeEntity.ForwardSlash} ${original.totalCount}`
                                : original.count}
                        </Tag>
                        <TaskStatusTag status={original.status} />
                    </Stack>
                );
            },
            meta: {
                align: 'start',
                width: 250,
            },
        }),
        columnHelper.accessor('notes', {
            header: () => <LocalizedText textKey={TextKey.GoLiveAuditsTableColumnObservations} />,
            cell: ({ getValue, row }) => (
                <Stack spacing="100" vertical>
                    <MarkdownNotes observations={getValue()} />
                    {row.original.onboardingManagerNotes && (
                        <Stack vertical>
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {lt('GoLiveAuditsTableColumnNotes')}
                            </Typography>
                            <Body size="small">{row.original.onboardingManagerNotes}</Body>
                        </Stack>
                    )}
                </Stack>
            ),
            meta: {
                align: 'start',
            },
        }),
    ];

    if (!inDialog) {
        columns.push(
            columnHelper.display({
                id: 'actions',
                meta: { align: 'end' },
                cell: ({ row: { original } }) => {
                    return <RowActionCell fieldIdentifier={original.fieldIdentifier} />;
                },
            })
        );
    }

    return columns;
};
